import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { FormInput, FormTextarea } from "@/components/forms/FormControls";
import { contactInquirySchema } from "@/schemas/public";
import type { ContactInquiryValues } from "@/schemas/public";
import { usePostContactInquiry } from "@/hooks/useBilling";
import { PlanInterest } from "@/enum/common.enum";

interface HelpContactModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Help Center's "Send email" form.
 *
 * Posts to the same `POST /billing/contact` the pricing inquiry uses — it
 * already persists to `db.contact_inquiries` and emails the sales destination,
 * so a second endpoint would be the same behaviour under a different name.
 * `plan_interest` is pinned to "other" because this is a support message, not a
 * pricing lead, and that keeps the two distinguishable in the inbox.
 */
export default function HelpContactModal({ open, onOpenChange }: HelpContactModalProps) {
  const [sent, setSent] = useState(false);
  const { mutate: postContactInquiry, isPending } = usePostContactInquiry();

  const form = useForm<ContactInquiryValues>({
    resolver: zodResolver(contactInquirySchema),
    defaultValues: {
      name: "", email: "", company: "", team_size: "",
      plan_interest: PlanInterest.OTHER, message: "",
    },
  });

  const close = (next: boolean) => {
    onOpenChange(next);
    if (!next) {
      // Reset on close so reopening isn't a stale success screen.
      setSent(false);
      form.reset();
    }
  };

  const submit = (values: ContactInquiryValues) =>
    postContactInquiry(values, { onSuccess: () => setSent(true) });

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-md" data-testid="help-contact-modal">
        {sent ? (
          <div className="py-6 text-center" data-testid="help-contact-sent">
            <CheckCircle2 className="mx-auto mb-3 size-10 text-dash-greenMid" strokeWidth={1.5} />
            <h2 className="text-base font-semibold text-dash-ink">Message sent</h2>
            <p className="mx-auto mt-2 max-w-xs text-sm text-dash-muted">
              We&apos;ll email you back within one business day.
            </p>
            <Button type="button" variant="dashboardPrimary" className="mt-6" onClick={() => close(false)}>
              Done
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Email support</DialogTitle>
              <DialogDescription>
                Tell us what you&apos;re stuck on and a person will get back to you.
              </DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(submit)} className="mt-2 flex flex-col gap-4" noValidate>
                <FormInput name="name" label="Name" data-testid="help-contact-name" required />
                <FormInput name="email" label="Email" type="email" data-testid="help-contact-email" required />
                <FormTextarea name="message" label="How can we help?" rows={5} data-testid="help-contact-message" />
                <div className="mt-2 flex justify-end gap-2">
                  <Button type="button" variant="dashboardOutline" onClick={() => close(false)}>Cancel</Button>
                  <Button type="submit" variant="dashboardPrimary" loading={isPending} data-testid="help-contact-submit">
                    Send message
                  </Button>
                </div>
              </form>
            </Form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
